'use client';

import { useRouter } from 'next/navigation';
import Image from 'next/image';
import LearningPathEnrollButton from '@/components/common/learning-path-enroll-button';
import { CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';

interface LearningPathTabCardProps {
  uid: string;
  url: string;
  thumbnail?: string;
  title: string;
  description: string;
  isEnrolled?: boolean;
  hasStarted?: boolean;
  progressPercent?: number;
  isCompleted?: boolean;
  isLearningMaterialCompleted?: boolean;
  hasAssessment?: boolean;
}

export default function LearningPathTabCard({
  uid,
  url,
  thumbnail,
  title,
  description,
  isEnrolled = false,
  hasStarted = false,
  progressPercent,
  isCompleted,
  isLearningMaterialCompleted = false,
  hasAssessment = false,
}: LearningPathTabCardProps) {
  const router = useRouter();
  const baseUrl = url.startsWith('/') ? url : `/${url}`;
  const navigateUrl = hasAssessment && isLearningMaterialCompleted && !isCompleted
    ? `${baseUrl}/assessment`
    : baseUrl;

  return (
    <div
      onClick={() => router.push(baseUrl)}
      className="group flex w-full cursor-pointer flex-col items-stretch sm:h-[166px] sm:flex-row"
      style={{
        background:
          'linear-gradient(150.65deg, color-mix(in srgb, var(--Color-Extended-Amethyst-Amethyst-Accent) 30%, transparent) -120.65%, color-mix(in srgb, var(--Color-Extended-Amethyst-Amethyst-Accent) 10.5%, transparent) -66.7%, transparent 59.17%), linear-gradient(180deg, transparent 0%, color-mix(in srgb, var(--Color-Grayscale-950-Bold) 10%, transparent) 100%), var(--Color-Grayscale-925-Bold)',
      }}
    >
      {/* Thumbnail */}
      <div className="relative h-[200px] w-full flex-shrink-0 overflow-hidden sm:h-[166px] sm:w-[295.11px]">
        <Image
          src={
            thumbnail && thumbnail.trim() !== ''
              ? thumbnail
              : CARD_FALLBACK_IMAGE
          }
          alt={title}
          fill
          className="object-cover"
          sizes="(max-width: 640px) 100vw, 295px"
        />
      </div>

      {/* Content */}
      <div
        className="flex flex-grow bg-surface-3 flex-col items-start sm:h-[166px] sm:flex-row sm:items-center"
        style={{
          padding: 'var(--Spacings-space-m)',
          gap: 'var(--Spacings-space-xs)',
        }}
      >
        {/* Copy Container */}
        <div
          className="flex flex-grow flex-col items-start justify-center"
          style={{ gap: 'var(--Spacings-space-xs)' }}
        >
          {/* Title Container */}
          <div className="flex flex-col items-start justify-center">
            {/* Learning Path Label */}
            <span className="cs-caption" style={{ color: 'var(--Text-Default-Light)' }}>
              Learning Path
            </span>

            {/* Title */}
            <h3 
              className="cs-h3 line-clamp-1" 
              style={{ 
                color: 'var(--Text-Default-Strong)', 
                margin: 0
              }}
            >
              {title}
            </h3>
          </div>

          {/* Description */}
          <p
            className="cs-m-body line-clamp-2"
            style={{
              color: 'var(--Text-Default-Medium)',
              margin: 0,
            }}
          >
            {description}
          </p>
        </div>

        {/* Enroll Button */}
        <div className="flex flex-shrink-0 flex-col items-start justify-end sm:items-end">
          <div onClick={(e) => e.stopPropagation()}>
            <LearningPathEnrollButton
              learningPathUid={uid}
              initialIsSubscribed={isEnrolled}
              initialHasStarted={hasStarted}
              progressPercent={progressPercent}
              isCompleted={isCompleted}
              isLearningMaterialCompleted={isLearningMaterialCompleted}
              hasAssessment={hasAssessment}
              navigateUrl={navigateUrl}
              variant="PrimaryFillButton"
              className="min-w-[120px] justify-center btn-primary-fill-group-hover btn-icon-move"
              showIcon={true}
              buttonText={{
                notEnrolled: 'Enroll',
                enrolling: 'Enrolling...',
                enrolled: 'Start',
                enrolledAndStarted: 'Resume Learning',
                takeAssessment: 'Take Assessment',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
